import T from 'prop-types'
import { toPythonStyle } from '@xhs/data-transform'
import { commonChecks } from '@xhs/ozone-detector'
import { invoke } from '@xhs/ozone-schema'
import { adapter, callbackAdapter } from './libs/bridgeAdapter'
import {
  canShareMp,
  changeTitle as changeTitleMp,
  setShareInfo as setShareInfoMp,
} from './libs/mpBridge'
import { resultType, urlType } from './libs/validators'
import { appVersionGte } from './libs/version'
import isSupport from './libs/isSupport'
import callbacks from './libs/callbacks'

import { warnDeprecated, errorDeprecated } from './utils/logDeprecated'
import { checkArgs } from './utils/checks'
import { arrangeExtensionFriend } from './utils/shareExtensionFormat'
import BridgeError from './utils/BridgeError'

/**
 * Deprecated
 */
export function toast() {
  errorDeprecated('toast', 'showToast')
}

export function showalert() {
  errorDeprecated('showalert', 'showAlert')
}

export function toggleNativeLoading(args) {
  warnDeprecated('toggleNativeLoading', 'disableNativeLoading')

  const spec = {
    argsT: T.shape({
      on: T.bool.isRequired,
    }),
  }

  return adapter('toggleNativeLoading', args, spec)
}

/**
 * UI
 */
export function changeTitle(title) {
  warnDeprecated('changeTitle', 'document.title = \'my title\'')

  const spec = {
    argsT: T.string.isRequired,
  }

  if (commonChecks.isMiniprogram) {
    return changeTitleMp(title)
  }

  return adapter('changeTitle', title, spec)
}

/**
 * 弹框
 */
export function showToast(message, config = {}) {
  const spec = {
    argsT: T.string.isRequired,
  }

  checkArgs(message, spec.argsT, 'showToast')

  if (message === undefined || message === null) {
    throw new BridgeError('ERR_ARGS_NULL', 'message should not be undefined or null')
  }

  return adapter('toast', { message, ...config })
}

export function showAlert(data) {
  const bridgeName = 'showalertV2'
  const spec = {
    argsT: T.shape({
      title: T.string,
      desc: T.string,
      actions: T.arrayOf(T.shape({
        name: T.string,
        callback: T.func,
      })),
    }),
  }

  checkArgs(data, spec.argsT, 'showAlert')

  // BUGFIX: Android use 'des' as 'desc'
  const cloneData = { ...data, des: data.desc || '' }
  const formattedActions = []
  const ACTION_STYPE_MAP_IOS = {
    default: 0,
    cancel: 1,
    destructive: 2,
  }

  cloneData.actions.forEach(action => {
    const callbackName = callbacks.add(bridgeName, action.callback)

    formattedActions.push({
      name: action.name,
      type: ACTION_STYPE_MAP_IOS[action.style], // iOS only
      script: `window.${callbackName} && window.${callbackName}`, // native 执行时候会往后面再添加一个 ‘()’
    })
  })
  cloneData.actions = formattedActions

  return adapter(bridgeName, cloneData)
}

export function showActionSheet(data) {
  const spec = {
    argsT: T.shape({
      title: T.string,
      desc: T.string,
      actions: T.arrayOf(T.shape({
        name: T.string,
        value: T.string.isRequired,
      })),
    }),
    resT: T.shape({
      result: resultType,
      value: T.string,
    }),
  }

  return adapter('showActionSheet', data, spec)
}

/**
 * 导航
 */
// FIXME: setNaviBackCallback should be a event rather than a bridge
export function setNaviBackCallbackLegacy(callback) {
  return callbackAdapter('setNaviBackCallback', undefined, callback)
}

export function setNaviBackCallback(callback) {
  warnDeprecated('setNaviBackCallback', 'subscribe(\'navBack\', () => {})')

  return setNaviBackCallbackLegacy(callback)
}

export function setNavigationHidden() {
  const spec = {
    resT: T.shape({
      result: resultType,
    }),
  }

  return adapter('setNavigationHidden', spec)
}

export function showNavigationRightBarButtonItem(config) {
  const spec = {
    argsT: T.shape({
      visible: T.bool,
      buttonTitle: T.string,
      buttonIcon: T.string,
      handler: T.func,
    }),
    resT: T.shape({
      result: resultType,
    }),
  }

  if (config.button_title) {
    warnDeprecated('args.button_title', 'args.buttonTitle')
  }

  if (config.button_icon) {
    warnDeprecated('args.button_icon', 'args.buttonIcon')
  }

  checkArgs(config, spec.argsT, 'showNavigationRightBarButtonItem')

  const { handler, ...other } = config
  const newConfig = toPythonStyle(other)

  // FIXME: use standard callback
  if (handler && typeof handler === 'function') {
    window.XHSHandler = {
      ...window.XHSHandler,
      navigationRightBarButtonItem: handler,
    }
  }

  return adapter(
    'showNavigationRightBarButtonItemV2',
    newConfig,
    { resT: spec.resT },
  )
}

/**
 * 状态栏
 */
export function setStatusBarHiddenIOS(invisible = true) {
  const spec = {
    argsT: T.bool.isRequired,
  }

  return adapter('setStatusBarHiddenIOS', invisible, spec)
}

export function setStatusBarStyleIOS(style) {
  warnDeprecated('setStatusBarStyleIOS', 'setStatusBarTextColor')

  const spec = {
    argsT: T.oneOf([0, 1]).isRequired,
  }

  return invoke('setStatusBarStyleIOS', { style }).catch(() => adapter('setStatusBarStyleIOS', style, spec))
}

export function setStatusBarTextColor(color) {
  const colorMap = {
    white: 0,
    black: 1,
  }

  const newColor = colorMap[color]

  if (commonChecks.isIOS) {
    return adapter('setStatusBarStyleIOS', newColor)
  }

  return adapter('setStatusBarTextColor', newColor)
}

/**
 * 控制 iOS 顶部回弹效果
 */
export function alwaysBounceIOS(direction, bounces = true) {
  const spec = {
    argsT: T.shape({
      direction: T.oneOf(['vertical', 'horizontal']).isRequired,
      bounces: T.bool.isRequired,
    }),
  }

  return adapter('alwaysBounceIOS', { direction, bounces }, spec)
}

export function enableBouncesIOS(...args) {
  warnDeprecated('enableBouncesIOS', 'alwaysBounceIOS')

  return alwaysBounceIOS(...args)
}

/**
 * 分享
 */
// FIXME: 不传参数表示隐藏分享按钮
export function setShareInfo(data) {
  const spec = {
    argsT: T.shape({
      title: T.string,
      content: T.string,
      linkurl: urlType,
      imageurl: urlType,
      type: T.string,
      extension: T.shape({
        miniprogram: T.shape({
          title: T.string,
          desc: T.string,
          webpageurl: urlType,
          path: T.string,
          thumb: T.string,
          username: T.string,
        }),
        friend: T.oneOfType([
          T.string,
          T.shape({
            source: T.string,
            type: T.oneOf([
              'general',
              'goodsDetail',
              'center',
              'topic',
              'event',
            ]),
            title: T.string,
            content: T.string,
            cover: T.string,
            image: T.string,
            link: T.string,
            btn: T.string,
            avatar: T.string,
            brandName: T.string,
            id: T.string,
            desc: T.string,
            price: T.number,
          }),
        ]),
      }),
    }),
    resT: T.shape({
      result: resultType,
    }),
  }

  let newData = data

  // data maybe undefined
  if (newData) {
    newData = { ...newData }

    if (newData.type && !canShareMp) {
      newData.type = newData.type.replace(/WXMiniProgram/, 'WeixinSession')
    }

    if (newData.image && !newData.imageurl) {
      warnDeprecated('args.image', 'args.imageurl')

      newData.imageurl = newData.image
    }

    // handle friend data
    const friend = arrangeExtensionFriend(newData.extension?.friend)

    if (friend) {
      newData.extension.friend = friend
    }
  }

  if (commonChecks.isMiniprogram) {
    return setShareInfoMp(newData)
  }

  return adapter('setShareInfo', newData, spec)
}

// 内容来自 setShareInfo
export function showShareMenu() {
  return adapter('showShareMenu')
}

export function shareContent(data) {
  const spec = {
    argsT: T.shape({
      type: T.string.isRequired,
      contentType: T.oneOf(['link', 'image', 'text']),
      title: T.string,
      content: T.string,
      linkurl: urlType,
      imageurl: urlType,
      base64string: T.string,
      extension: T.shape({
        miniprogram: T.shape({
          title: T.string,
          desc: T.string,
          webpageurl: urlType,
          path: T.string,
          thumb: T.string,
          username: T.string,
        }),
        friend: T.oneOf([T.string, {
          source: T.string,
          type: T.oneOf([
            'general',
            'goodsDetail',
            'center',
            'topic',
            'event',
          ]),
          title: T.string,
          content: T.string,
          cover: T.string,
          image: T.string,
          link: T.string,
          btn: T.string,
          avatar: T.string,
          brandName: T.string,
          id: T.string,
          desc: T.string,
          price: T.number,
        }]),
      }),
    }),
    resT: T.shape({
      result: resultType,
    }),
  }

  const newData = { ...data }

  if (newData.type && !canShareMp) {
    newData.type = data.type.replace(/WXMiniProgram/, 'WeixinSession')
  }

  if (newData.image && !newData.imageurl) {
    warnDeprecated('args.image', 'args.imageurl')

    newData.imageurl = newData.image
  }

  // handle friend data
  const friend = arrangeExtensionFriend(newData.extension?.friend)

  if (friend) {
    newData.extension.friend = friend
  }

  return adapter('shareContentV2', newData, spec)
}

/**
 * 支付
 */
export function pay(config) {
  return adapter('pay', config)
}

export function alipayClient(data) {
  const spec = {
    argsT: T.string.isRequired,
    resT: T.shape({
      result: resultType,
      orderid: T.string.isRequired,
    }),
  }

  return adapter('alipayClient', data, spec)
}

export function openURLByWechat(data) {
  const spec = {
    argsT: T.shape({
      url: T.string.isRequired,
    }),
  }

  return adapter('openURLByWechat', data, spec)
}

export function wechatPayClient(data) {
  const spec = {
    argsT: T.string.isRequired,
    resT: T.shape({
      result: resultType,
      orderid: T.string.isRequired,
    }),
  }

  return adapter('wechatPayClient', data, spec)
}

export function supportApplePayIOS() {
  return adapter('supportApplePay')
}

export function applePayClientIOS(orderId) {
  return adapter('applePayClient', orderId)
}

/**
 * Link
 */
export function openLinkAndroid(link) {
  const spec = {
    argsT: T.string.isRequired,
  }

  return adapter('openLink', link, spec)
}

export function openLink(link) {
  warnDeprecated('openLink', 'openLinkAndroid')

  return openLinkAndroid(link)
}

// iOS 的 closeWindow 是从栈顶移除，Android 是移除当前 Activity
export function closeWindow() {
  return adapter('closeWindow')
}

// 移除当前 ViewController
function removeVCFromStackIOS() {
  return adapter('removeVCFromStack')
}

export function replaceSelfWithLink(link, typeIOS = 'push') { // 'present'
  const supportTypeIOS = commonChecks.isIOS && appVersionGte('5.39')

  const spec = {
    argsT: supportTypeIOS
      ? { link: T.string.isRequired, type: T.oneOf(['push', 'present']).isRequired }
      : T.string.isRequired,
  }

  if (supportTypeIOS) {
    return adapter('replaceSelfWithLink', { link, type: typeIOS }, spec)
  }

  return adapter('replaceSelfWithLink', link, spec)
}

// Android 关闭前一个页面时，会处理 H5 路由，而 iOS 不会，V2 和 iOS 表现一致
export function replaceSelfWithLinkV2Android(link) {
  return adapter('replaceSelfWithLinkV2', { link })
}

export function replaceRouteWith(newLink) {
  if (commonChecks.isIOS && appVersionGte('6.54')) {
    return replaceSelfWithLink(newLink)
  }

  if (commonChecks.isAndroid && appVersionGte('6.56')) {
    return replaceSelfWithLinkV2Android(newLink)
  }

  if (commonChecks.isIOS && !isSupport('removeVCFromStack')) {
    return replaceSelfWithLink(newLink)
  }

  return new Promise((resolve, reject) => {
    window.open(newLink)

    setTimeout(() => {
      if (commonChecks.isIOS) {
        removeVCFromStackIOS()
          .then(resolve)
          .catch(reject)
      } else {
        window.close()
        resolve()
      }
    }, 300)
  })
}

// 直播间内本层webview跳转到礼物面板（关闭当前webview，直播间外调用无响应）
// 6.74 版本支持新通道
export async function openGiftPanel(params) {
  try {
    const res = await invoke('openGiftPanel', params)
    return res
  } catch (error) {
    return adapter('openGiftPanel')
  }
}

export function setPasteBoard(string) {
  const spec = {
    argsT: T.string.isRequired,
    resT: T.shape({
      result: resultType,
    }),
  }

  checkArgs(string, spec.argsT, 'setPasteBoard')

  return adapter('setPasteBoard', { string }, { resT: spec.resT })
}

/**
 * 打点
 */
export function showTrack(data) {
  const spec = {
    argsT: T.shape({
      title: T.string,
      content: T.string,
      isNewTrack: T.bool,
    }),
  }

  return adapter('showTrack', data, spec)
}

/**
 * APM打点
 */
export function showApmTrack(data) {
  const spec = {
    argsT: T.shape({
      content: T.string,
    }),
  }

  return adapter('showApmTrack', data, spec)
}

export function webtrack(data) {
  const spec = {
    argsT: T.object,
  }

  return adapter('webtrack', data, spec)
}

export function emitTrack(base64string) {
  const spec = {
    argsT: T.string.isRequired,
  }

  return adapter('emitTrack', base64string, spec)
}

export function emitApmTrack(base64string) {
  const spec = {
    argsT: T.string.isRequired,
  }

  return adapter('emitApmTrack', base64string, spec)
}

export function openMapWithLocation(data) {
  const spec = {
    argsT: T.shape({
      lat: T.number,
      long: T.number,
      direction: T.bool,
      name: T.string,
      coordinate: T.shape({
        wgs84: T.shape({
          long: T.number,
          lat: T.number,
        }),
        gcj02: T.shape({
          long: T.number,
          lat: T.number,
        }),
        bd09: T.shape({
          long: T.number,
          lat: T.number,
        }),
      }),
    }),
    resT: T.shape({
      result: resultType,
      type: T.oneOf(['apple', 'baidu', 'amap', 'google']).isRequired,
    }),
  }

  return adapter('openMapWithLocation', data, spec)
}

/**
 * anti-spam
 */
export function confirmAntiSpam() {
  return adapter('confirmAntiSpam')
}

export function addComment(data) {
  const spec = {
    argsT: T.shape({
      placeholder: T.string,
      uid: T.string,
    }),
    resT: T.shape({
      uid: T.string,
      is_send: T.bool,
      data: T.shape({
        content: T.string,
        at_users: T.arrayOf(T.shape({
          userid: T.string,
          nickname: T.string,
        })),
      }),
    }),
  }

  return adapter('addComment', data, spec)
}

export function openXhsSystemSettings() {
  return adapter('openXhsSystemSettings')
}

export function openRechargeCoinPanel() {
  return adapter('openRechargeCoinPanel')
}

export function openFansPanel() {
  return adapter('openFansPanel')
}
